// features/pharmacy/BatchSelectionPanel.tsx
// The two pieces of near-expiry UI every dispensing screen renders —
// DispensingTab.tsx, PharmacyStage.tsx and the walk-in OutpatientTab.tsx —
// so the warning and the reason prompt look and behave the same wherever
// stock is sold. The rules themselves live in batchSelection.ts.
import { useState } from 'react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input, NativeSelect } from '@/components/ui/Input';
import { daysToExpiry, isNearExpiry, type BatchSelectionLine, type LaterBatchIssue } from './batchSelection';
import type { BatchSelectionReason } from './types';

const fmtDate = (d: string) => new Date(d).toLocaleDateString('en-IN');

/**
 * "Near expiry" warning for one batch — renders nothing outside the window.
 * A warning only: it never blocks the sale.
 */
export function NearExpiryBadge({ expiry, nearExpiryDays, className }: { expiry: string | null | undefined; nearExpiryDays: number; className?: string }) {
  if (!isNearExpiry(expiry, nearExpiryDays)) return null;
  const days = daysToExpiry(expiry) ?? 0;
  return (
    <Badge
      variant="caution"
      className={className}
      title={`Expires on ${fmtDate(String(expiry))} — inside the hospital's ${nearExpiryDays}-day near-expiry window. Check the patient can finish it before then.`}
    >
      ⏳ Near expiry · {days === 0 ? 'today' : `${days} day${days === 1 ? '' : 's'}`}
    </Badge>
  );
}

/** Label for a stored reason code, falling back to the code itself. */
export function batchReasonLabel(value: string | null | undefined, reasons: BatchSelectionReason[]): string {
  if (!value) return '';
  return reasons.find((r) => r.value === value)?.label ?? value;
}

type NamedLine = BatchSelectionLine & { name: string; batch: string };

/**
 * The "a later-expiry batch was chosen" banner: which lines, which earlier
 * batch could have been used, and one shared reason (+ remarks) applied to
 * all of them — the same one-field-for-the-cart pattern as the quantity-
 * override and off-prescription banners next to it. `onUseEarlier`, when
 * given, offers switching the line back to the earlier batch instead.
 */
export function LaterBatchPanel<L extends NamedLine>({
  issues, reasons, onApply, onUseEarlier, className,
}: {
  issues: LaterBatchIssue<L>[];
  reasons: BatchSelectionReason[];
  onApply: (inventoryIds: Set<number>, reason: string, remarks: string) => void;
  onUseEarlier?: (line: L, earlierInventoryId: number) => void;
  className?: string;
}) {
  const [reason, setReason] = useState('');
  const [remarks, setRemarks] = useState('');
  if (!issues.length) return null;

  const needsRemarks = reason === 'other' && !remarks.trim();
  const apply = () => {
    if (!reason || needsRemarks) return;
    onApply(new Set(issues.map((i) => i.line.inventory_id)), reason, remarks);
    setReason('');
    setRemarks('');
  };

  return (
    <div className={`rounded-[var(--radius)] border border-danger-soft bg-danger-soft px-3 py-2 text-[12px] text-danger-ink ${className ?? ''}`}>
      <div className="text-[10px] font-bold uppercase tracking-wide">Reason needed — a later-expiry batch was chosen</div>
      {issues.map(({ line, earlier }) => (
        <div key={line.inventory_id} className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
          <span>
            <strong>{line.name}</strong> batch <strong>{line.batch}</strong> (exp {fmtDate(line.expiry)}) — batch{' '}
            <strong>{earlier.batch_number}</strong> expires earlier ({fmtDate(earlier.expiry_date)}) and has{' '}
            {earlier.quantity_available} in stock.
          </span>
          {onUseEarlier && (
            <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px]" onClick={() => onUseEarlier(line, earlier.id)}>
              Use batch {earlier.batch_number}
            </Button>
          )}
        </div>
      ))}
      <div className="mt-2 flex flex-wrap gap-1.5">
        <NativeSelect className="min-w-[220px] flex-1" value={reason} onChange={(e) => setReason(e.target.value)} aria-label="Reason for choosing a later-expiry batch">
          <option value="">Select a reason…</option>
          {reasons.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
        </NativeSelect>
        <Input
          className="min-w-[180px] flex-1"
          placeholder={reason === 'other' ? 'Remarks (required for Other)' : 'Remarks (optional)'}
          maxLength={500}
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
        />
        <Button size="sm" disabled={!reason || needsRemarks} onClick={apply}>Apply</Button>
      </div>
    </div>
  );
}

/** One-line summary of a recorded reason, for a cart row's detail line. */
export function batchSelectionSummary(line: Pick<BatchSelectionLine, 'batch_selection_reason' | 'batch_selection_remarks'>, reasons: BatchSelectionReason[]): string {
  if (!line.batch_selection_reason) return '';
  const label = batchReasonLabel(line.batch_selection_reason, reasons);
  return line.batch_selection_remarks ? `${label} — ${line.batch_selection_remarks}` : label;
}
